import React, { useEffect, useState } from "react";
import axios from "../axiosInstance"; 

export default function Profile() {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [message, setMessage] = useState("");

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Fetch profile + states
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}user/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setUser(res.data);
        setFormData({
          id: res.data.id,
          first_name: res.data.first_name || "",
          last_name: res.data.last_name || "",
          email: res.data.email || "",
          mobile_no: res.data.mobile_no || "",
          stateId: res.data.state?.id || "",
          cityId: res.data.city?.id || "",
        });

        // fetch states
        const stateRes = await axios.get(
          `${process.env.REACT_APP_BASE_URL}states`
        );
        setStates(stateRes.data);

        if (res.data.state?.id) {
          const cityRes = await axios.get(
            `${process.env.REACT_APP_BASE_URL}cities?stateId=${res.data.state.id}&paginate=false`
          );
          setCities(cityRes.data);
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
      }
    };

    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleStateChange = async (e) => {
    const newStateId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      stateId: newStateId,
      cityId: "",
    }));

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}cities?stateId=${newStateId}&paginate=false`
      );
      setCities(res.data);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}user/update-profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUser(res.data.data);
      setIsEditing(false);
      setMessage("Profile updated successfully ✅");
    } catch (err) {
      console.error("Error updating profile:", err);
      setMessage("Failed to update profile ❌");
    }
  };

  if (!user) {
    return (
      <p className="p-6 text-lg text-gray-500 font-medium">Loading profile...</p>
    );
  }

  return (
    <div className="p-6 w-full min-h-screen font-[Poppins] flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="w-full max-w-6xl bg-white shadow-2xl rounded-2xl flex overflow-hidden min-h-[85vh]">
        {/* LEFT PANEL */}
        <div className="w-1/3 bg-gradient-to-br from-pink-600 to-orange-400 text-white flex flex-col items-center justify-center p-10 relative">
          <div className="w-32 h-32 rounded-full bg-white flex items-center justify-center text-5xl font-bold text-gray-700 mb-6 shadow-xl ring-4 ring-pink-200">
            {user.first_name?.charAt(0) || "U"}
          </div>
          <h2 className="text-3xl font-bold tracking-wide">
            {user.first_name} {user.last_name}
          </h2>
          <p className="text-lg opacity-90 mt-2 capitalize">
            {user.user_type}
          </p>

          <button
            onClick={() => setIsEditing(true)}
            className="mt-8 px-8 py-3 bg-white text-pink-600 rounded-xl font-semibold shadow-lg hover:bg-gray-100 hover:scale-105 transition-all duration-200"
          >
            ✏️ Edit Profile
          </button>
        </div>

        {/* RIGHT PANEL */}
        <div className="w-2/3 p-10 space-y-10 flex flex-col justify-center h-full">
          {/* First + Last Name */}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                First Name
              </h3>
              {isEditing ? (
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="w-full border rounded-xl px-4 py-3 text-base shadow-sm focus:ring-2 focus:ring-pink-400 outline-none"
                />
              ) : (
                <p className="text-gray-800 text-lg font-semibold">
                  {user.first_name}
                </p>
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                Last Name
              </h3>
              {isEditing ? (
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full border rounded-xl px-4 py-3 text-base shadow-sm focus:ring-2 focus:ring-pink-400 outline-none"
                />
              ) : (
                <p className="text-gray-800 text-lg font-semibold">
                  {user.last_name}
                </p>
              )}
            </div>
          </div>

          {/* Email (Read-only) */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Email
            </h3>
            <p className="text-gray-800 text-lg font-semibold">{user.email}</p>
          </div>

          {/* Phone */}
          <div>
            <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
              Phone
            </h3>
            {isEditing ? (
              <input
                type="text"
                name="mobile_no"
                value={formData.mobile_no}
                onChange={handleChange}
                className="w-full border rounded-xl px-4 py-3 text-base shadow-sm focus:ring-2 focus:ring-pink-400 outline-none"
              />
            ) : (
              <p className="text-gray-800 text-lg font-semibold">
                {user.mobile_no}
              </p>
            )}
          </div>

          {/* State + City */}
          <div className="grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                State
              </h3>
              {isEditing ? (
                <select
                  name="stateId"
                  value={formData.stateId}
                  onChange={handleStateChange}
                  className="w-full border rounded-xl px-4 py-3 text-base shadow-sm bg-white focus:ring-2 focus:ring-pink-400 outline-none"
                >
                  <option value="">-- Select State --</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-gray-800 text-lg font-semibold">
                  {user.state?.name}
                </p>
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">
                City
              </h3>
              {isEditing ? (
                <select
                  name="cityId"
                  value={formData.cityId}
                  onChange={handleChange}
                  disabled={!formData.stateId}
                  className="w-full border rounded-xl px-4 py-3 text-base shadow-sm bg-white focus:ring-2 focus:ring-pink-400 outline-none"
                >
                  <option value="">-- Select City --</option>
                  {cities.map((city) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-gray-800 text-lg font-semibold">
                  {user.city?.name}
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          {isEditing && (
            <div className="flex gap-6 pt-4">
              <button
                onClick={handleSave}
                className="px-8 py-3 bg-blue-600 text-white rounded-xl font-semibold shadow-lg hover:bg-blue-700 hover:scale-105 transition-all duration-200"
              >
                💾 Save
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-8 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold shadow hover:bg-gray-300 hover:scale-105 transition-all duration-200"
              >
                ✖ Cancel
              </button>
            </div>
          )}

          {/* Feedback message */}
          {message && (
            <div
              className={`mt-4 px-4 py-3 rounded-lg text-sm font-medium shadow ${
                message.includes("✅")
                  ? "bg-green-100 text-green-700 border border-green-300"
                  : "bg-red-100 text-red-700 border border-red-300"
              }`}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
